import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertController, IonicModule, IonModal, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { honapNev } from '../ber.opciok';
import { BerJuttatasApi, csvLetolt, ftSzoveg, HONAPOK_EVVEL, szazalek, tizedes } from '../juttatas/juttatas.api';
import { JuttatasParamComponent } from '../juttatas/juttatas-param.component';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';
import { SzerkZarSavComponent } from '../../components/szerk-zar/szerk-zar-sav.component';

/**
 *  BÉREN KÍVÜLI JUTTATÁSOK LISTÁJA (RLB: Számfejtés → Béren kívüli juttatások listája).
 *
 *  Két fül:
 *   - LISTA: az időszak (hónap vagy egész év) minden juttatása – cégtelefon, reprezentáció,
 *     SZÉP-kártya, egyéb – típusonkénti és (éves nézetben) havi összesítővel, nyomtatható, CSV.
 *     Itt rögzíthetők az „egyéb" juttatások is (csekély értékű ajándék, egyéb egyes
 *     meghatározott / béren kívüli); a cégtelefon és a reprezentáció a saját képernyőjén.
 *   - SZÉP-KÁRTYA: munkavállalónként az időarányos keret, az utalások és a kereten felüli rész.
 *     2023 óta nincsenek alszámlák: egy rekreációs keret van (450 000 Ft/év), mellette az
 *     „Aktív Magyarok" keret. A kereten felüli rész EGYES MEGHATÁROZOTT juttatásként adózik
 *     (NAV, 2022.12.30.), a juttatás hónapjának kötelezettségeként (NAV 49. füzet).
 */
@Component({
  selector: 'app-ber-juttatasok',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, JuttatasParamComponent, SzerkZarSavComponent],
  templateUrl: './juttatasok.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './juttatasok.component.scss'],
})
export class JuttatasokComponent implements OnInit, OnDestroy {
  @ViewChild('szerkModal') szerkModal: IonModal;
  @ViewChild('paramModal') paramModal: IonModal;
  /** Az itt szerkeszthető típusok (a cégtelefonnak és a reprezentációnak saját képernyője van). */
  static readonly SZERKESZTHETO = ['csekely_ajandek', 'egyeb_meghatarozott', 'egyeb_berenkivuli', 'szep_rekreacios', 'szep_aktiv'];
  static readonly SAJAT_OLDAL: Record<string, string> = { cegtelefon: '/ber/cegtelefon', reprezentacio: '/ber/reprezentacio', uzleti_ajandek: '/ber/reprezentacio' };

  ful: 'lista' | 'szep' = 'lista';
  fulek = [{ k: 'lista', c: 'Juttatások listája' }, { k: 'szep', c: 'SZÉP-kártya' }];
  evek: number[] = [];
  honapok = HONAPOK_EVVEL;
  honapNev = honapNev;
  ft = ftSzoveg;
  szazalek = szazalek;
  tizedes = tizedes;

  ho = 0;
  lista: any = null;          // /lista válasz
  szep: any = null;           // /szep válasz
  param: any = {};
  tipusok: any[] = [];
  alkalmazottak: any[] = [];
  kivas = false;
  szerk: any = null;
  mentes = false;

  constructor(private ber: BerService, private api: BerJuttatasApi, private toast: ToastController,
              private alert: AlertController, private router: Router, private szerkZar: SzerkZarService) {}

  /*  SZERKESZTÉSI ZÁR (components/szerk-zar) a meglévő tételre: egyszerre egy ember szerkesztheti.
   *  A sorszám a késve érkező foglalást dobja el, ha a szerkesztőt közben bezárták. */
  zarSor: SzerkZar | null = null;
  private zarSorsz = 0;
  private async zarFoglal(s: any) {
    const n = ++this.zarSorsz;
    this.zarSor?.felenged(); this.zarSor = null;
    const z = await this.szerkZar.foglal('ber', 'ber_juttatas', s.id, { ujratolt: () => this.sorUjratolt(s) });
    if (n !== this.zarSorsz) { z?.felenged(); return; }
    this.zarSor = z;
  }
  zarEngedi() { this.zarSorsz++; this.zarSor?.felenged(); this.zarSor = null; }
  /** Friss adat a nyitott szerkesztőbe (a tétel évének juttatásaiból). */
  private async sorUjratolt(s: any) {
    const ev = Number(s.ev) || Number(String(s.datum || '').slice(0, 4)) || this.aktiv.ev;
    const sorok: any[] = await this.api.get(`/juttatas/${s.ugyfel_id || this.aktiv.ugyfelId}?ev=${ev}`);
    const friss = sorok.find((x) => x.id === s.id);
    if (this.szerk?.id !== s.id) return;
    if (friss) this.szerk = { ...friss };
    else { this.hiba('A tételt közben törölték.'); await this.szerkModal.dismiss(); }
  }
  ngOnDestroy() { this.zarEngedi(); }

  async ngOnInit() {
    await this.ber.init();
    const ev = new Date().getFullYear();
    this.evek = [ev + 1, ev, ev - 1, ev - 2, ev - 3];
    this.ho = this.aktiv.ho;
    await this.betolt();
  }

  get aktiv() { return this.ber.aktiv; }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { this.ho = ho; if (ho) await this.ber.setAktiv({ ho }); await this.betolt(); }
  async fulValt(k: any) { this.ful = k; await this.betolt(); }
  get idoszak(): string { return this.ho ? `${this.aktiv.ev}. ${honapNev(this.ho).toLowerCase()}` : `${this.aktiv.ev}. év`; }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    try {
      const u = this.aktiv.ugyfelId;
      const [p, alk]: any[] = await Promise.all([
        this.api.get(`/param/${u}?ev=${this.aktiv.ev}`),
        this.ber.get('/alkalmazottak/' + u).catch(() => []),
      ]);
      this.param = Object.fromEntries((p.parameterek || []).map((x: any) => [x.kulcs, x.ertek]));
      this.kivas = !!p.kivas;
      this.alkalmazottak = (alk || []).filter((a: any) => !a.inaktiv);
      if (this.ful === 'lista') {
        this.lista = await this.api.get(`/lista/${u}?ev=${this.aktiv.ev}&ho=${this.ho}`);
        this.tipusok = this.lista.tipusok || [];
      }
      // A SZÉP-keretek a rögzítő ablak tájékoztatójához is kellenek.
      this.szep = await this.api.get(`/szep/${u}?ev=${this.aktiv.ev}`);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni.'); }
  }

  tipus(t: string) { return this.tipusok.find((x) => x.tipus === t) || { cim: t, kategoria: '' }; }
  get szerkesztheto(): any[] {
    const cimek: Record<string, string> = {
      csekely_ajandek: 'Csekély értékű ajándék', egyeb_meghatarozott: 'Egyéb egyes meghatározott juttatás',
      egyeb_berenkivuli: 'Egyéb béren kívüli juttatás', szep_rekreacios: 'SZÉP-kártya – rekreációs keret', szep_aktiv: 'SZÉP-kártya – Aktív Magyarok',
    };
    return JuttatasokComponent.SZERKESZTHETO.map((t) => ({ tipus: t, cim: cimek[t] }));
  }
  kategoriaCim(k: string): string {
    return k === 'berenkivuli' ? 'béren kívüli' : k === 'adomentes' ? 'adómentes' : 'egyes meghatározott';
  }
  szepE(t: string): boolean { return t === 'szep_rekreacios' || t === 'szep_aktiv'; }
  sajatOldal(t: string): string | null { return JuttatasokComponent.SAJAT_OLDAL[t] || null; }
  get szepFelul(): number { return (this.szep?.alkalmazottak || []).reduce((a: number, x: any) => a + x.rekreacios.kereten_felul + x.aktiv.kereten_felul, 0); }

  /* ── rögzítés / módosítás ── */
  uj(tipus = 'csekely_ajandek') {
    this.zarEngedi();
    const ho = this.ho || this.aktiv.ho;
    const ma = new Date();
    const nap = ma.getFullYear() === this.aktiv.ev && ma.getMonth() + 1 === ho ? ma.getDate() : 1;
    this.szerk = { tipus, alkalmazott_id: null, datum: `${this.aktiv.ev}-${String(ho).padStart(2, '0')}-${String(nap).padStart(2, '0')}`,
      megnevezes: '', ertek: null, adomentes: false, megjegyzes: '' };
    this.szerkModal.present();
  }
  modosit(s: any) {
    const oldal = this.sajatOldal(s.tipus);
    if (oldal) { this.router.navigateByUrl(oldal); return; }
    this.szerk = { ...s };
    this.szerkModal.present();
    this.zarFoglal(s);
  }
  /** A kiválasztott munkavállaló SZÉP-kerete (a rögzítő ablak tájékoztatójához). */
  szepKeret(s: any): any {
    if (!s || !this.szepE(s.tipus) || !s.alkalmazott_id) return null;
    const a = (this.szep?.alkalmazottak || []).find((x: any) => x.alkalmazott_id === s.alkalmazott_id);
    if (!a) return null;
    if (s.tipus === 'szep_rekreacios') return { keret: a.rekreacios.keret, utalt: a.rekreacios.utalt, nev: 'rekreációs keret (évi)' };
    const masodik = Number(String(s.datum || '').slice(5, 7)) > 6;
    return masodik ? { keret: a.aktiv.keret_2, utalt: a.aktiv.utalt_2, nev: 'Aktív Magyarok – II. félév' }
      : { keret: a.aktiv.keret_1, utalt: a.aktiv.utalt_1, nev: 'Aktív Magyarok – I. félév' };
  }
  get csekelyHatar(): number { return Math.round((this.param.minimalber || 0) * (this.param.csekely_ajandek_hanyad || 0)); }

  async ment() {
    const s = this.szerk;
    if (this.zarSor && !this.zarSor.irhato) return;
    if (!s.datum || !(Number(s.ertek) > 0)) { this.hiba('A dátum és az érték kötelező.'); return; }
    if (this.szepE(s.tipus) && !s.alkalmazott_id) { this.hiba('A SZÉP-utaláshoz válassz munkavállalót.'); return; }
    const b = { tipus: s.tipus, alkalmazott_id: s.alkalmazott_id || null, datum: s.datum, megnevezes: s.megnevezes,
      ertek: Number(s.ertek), adomentes: this.szepE(s.tipus) ? false : !!s.adomentes, megjegyzes: s.megjegyzes };
    this.mentes = true;
    try {
      if (s.id) await this.api.put(`/juttatas/sor/${s.id}`, b);
      else await this.api.post(`/juttatas/${this.aktiv.ugyfelId}`, b);
      await this.szerkModal.dismiss();
      this.ok('Mentve.');
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
    finally { this.mentes = false; }
  }

  async torol(s: any) {
    const a = await this.alert.create({
      header: 'Tétel törlése', message: `${s.datum} · ${s.tipus_cim} · ${ftSzoveg(s.ertek)} Ft`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive', handler: async () => {
        try { await this.api.del(`/juttatas/sor/${s.id}`); this.ok('Törölve.'); await this.betolt(); }
        catch (e: any) { this.hiba(e?.error?.error || 'A törlés nem sikerült.'); }
      } }],
    });
    await a.present();
  }

  async ujraszamol() {
    try {
      const r: any = await this.api.post(`/juttatas/${this.aktiv.ugyfelId}/ujraszamol?ev=${this.aktiv.ev}&ho=${this.ho}`, {});
      this.ok(`${r.tetelek} tétel újraszámolva az aktuális paraméterekkel.`);
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Az újraszámolás nem sikerült.'); }
  }

  /* ── nyomtatás és export ── */
  private kozteherOszlop() { return { k: this.kivas ? 'kiva_alap' : 'szocho', c: this.kivas ? 'KIVA-alapba' : 'Szocho', num: true }; }

  async nyomtatTetelek() {
    const sorok = this.lista?.sorok || [];
    const oszlopok = [
      { k: 'datum', c: 'Dátum' }, { k: 'tipus_cim', c: 'Juttatás', szeles: true }, { k: 'alkalmazott_nev', c: 'Munkavállaló' },
      { k: 'megnevezes', c: 'Megnevezés', szeles: true }, { k: 'ertek', c: 'Érték', num: true }, { k: 'kereten_felul', c: 'Kereten felül', num: true },
      { k: 'adoalap', c: 'Adóalap', num: true }, { k: 'szja', c: 'SZJA', num: true }, this.kozteherOszlop(),
    ];
    const o = this.lista?.osszesen || {};
    await this.api.nyomtat('Béren kívüli és egyes meghatározott juttatások', [['Időszak', this.idoszak]], oszlopok, sorok,
      (s, c) => (c.num ? ftSzoveg(s[c.k]) : String(s[c.k] ?? '') + (c.k === 'tipus_cim' && s.adomentes ? ' (adómentes)' : '')),
      (c) => (c.num ? ftSzoveg(o[c.k]) : ''));
  }

  async nyomtatOsszesito() {
    const sorok = this.lista?.tipusonkent || [];
    const oszlopok = [
      { k: 'cim', c: 'Juttatás', szeles: true }, { k: 'kategoria', c: 'Kategória' }, { k: 'db', c: 'Tétel', num: true },
      { k: 'ertek', c: 'Érték', num: true }, { k: 'kereten_felul', c: 'Kereten felül', num: true }, { k: 'adoalap', c: 'Adóalap', num: true },
      { k: 'szja', c: 'SZJA', num: true }, this.kozteherOszlop(),
    ];
    const o = this.lista?.osszesen || {};
    await this.api.nyomtat('Juttatások összesítője', [['Időszak', this.idoszak]], oszlopok, sorok,
      (s, c) => (c.k === 'kategoria' ? this.kategoriaCim(s.kategoria) : c.k === 'db' ? String(s.db) : c.num ? ftSzoveg(s[c.k]) : String(s[c.k] ?? '')),
      (c) => (c.k === 'db' ? String(o.db || 0) : c.num ? ftSzoveg(o[c.k]) : ''));
  }

  async nyomtatSzep() {
    const sorok = this.szep?.alkalmazottak || [];
    const oszlopok = [
      { k: 'nev', c: 'Munkavállaló', szeles: true }, { k: 'napok', c: 'Napok', num: true },
      { k: 'rk', c: 'Rekreációs keret', num: true }, { k: 'ru', c: 'Utalva', num: true }, { k: 'rf', c: 'Kereten felül', num: true },
      { k: 'ak', c: 'Aktív M. keret', num: true }, { k: 'au', c: 'Aktív M. utalva', num: true }, { k: 'af', c: 'Aktív M. felül', num: true },
      { k: 'szja', c: 'SZJA', num: true }, { k: 'szocho', c: this.kivas ? 'Szocho (KIVA: 0)' : 'Szocho', num: true },
    ];
    const szam = (a: any, k: string): number => ({
      rk: a.rekreacios.keret, ru: a.rekreacios.utalt, rf: a.rekreacios.kereten_felul, ak: a.aktiv.keret_1 + a.aktiv.keret_2,
      au: a.aktiv.utalt_1 + a.aktiv.utalt_2, af: a.aktiv.kereten_felul, szja: a.szja, szocho: a.szocho,
    } as Record<string, number>)[k] || 0;
    await this.api.nyomtat('SZÉP-kártya juttatások', [['Év', String(this.aktiv.ev)]], oszlopok, sorok,
      (a, c) => (c.k === 'nev' ? a.nev : c.k === 'napok' ? String(a.napok) : ftSzoveg(szam(a, c.k))),
      (c) => (['nev', 'napok'].includes(c.k) ? '' : ftSzoveg(sorok.reduce((s: number, a: any) => s + szam(a, c.k), 0))));
  }

  csv() {
    const sorok = this.lista?.sorok || [];
    csvLetolt(`juttatasok_${this.aktiv.ev}${this.ho ? '_' + String(this.ho).padStart(2, '0') : ''}.csv`, [
      ['Dátum', 'Juttatás', 'Kategória', 'Munkavállaló', 'Adóazonosító', 'Megnevezés', 'Érték', 'Kereten felül', 'Adóalap', 'SZJA', 'Szocho', 'KIVA-alap', 'Adómentes'],
      ...sorok.map((s: any) => [s.datum, s.tipus_cim, this.kategoriaCim(s.kategoria), s.alkalmazott_nev || '', s.adoazonosito || '', s.megnevezes || '',
        Math.round(s.ertek), Math.round(s.kereten_felul), Math.round(s.adoalap), Math.round(s.szja), Math.round(s.szocho), Math.round(s.kiva_alap), s.adomentes ? 'igen' : '']),
    ]);
  }

  async parameterek() { await this.paramModal.present(); }
  async paramMentve() { await this.paramModal.dismiss(); await this.betolt(); }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'danger' })).present(); }
}
